'use client';

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

/**
 * Contexto para gerenciar estado de modais full-screen no DroneOps
 * 
 * Usado para:
 * - Desabilitar pull-to-refresh quando modais estão abertos
 * - Gerenciar pilha de modais (nested modals)
 * - Controlar comportamento de scroll/gestos em modais
 */

interface ModalStateContextValue {
  /** Indica se há pelo menos um modal full-screen aberto */
  hasOpenModal: boolean;
  
  /** Número de modais abertos (para suportar nested modals) */
  modalCount: number;
  
  /** Registra abertura de um modal */
  openModal: (modalId: string) => void;
  
  /** Registra fechamento de um modal */
  closeModal: (modalId: string) => void;
  
  /** Lista de IDs dos modais atualmente abertos */
  openModals: string[];
}

const ModalStateContext = createContext<ModalStateContextValue | null>(null);

export function ModalStateProvider({ children }: { children: ReactNode }) {
  const [openModals, setOpenModals] = useState<string[]>([]);

  const openModal = useCallback((modalId: string) => {
    setOpenModals((prev) => {
      // Evitar duplicatas
      if (prev.includes(modalId)) return prev;
      return [...prev, modalId];
    });
  }, []);

  const closeModal = useCallback((modalId: string) => {
    setOpenModals((prev) => prev.filter((id) => id !== modalId));
  }, []);

  const value: ModalStateContextValue = {
    hasOpenModal: openModals.length > 0,
    modalCount: openModals.length,
    openModal,
    closeModal,
    openModals,
  };

  return (
    <ModalStateContext.Provider value={value}>
      {children}
    </ModalStateContext.Provider>
  );
}

/**
 * Hook para acessar estado de modais
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { hasOpenModal } = useModalState();
 *   
 *   return (
 *     <PullToRefresh disabled={hasOpenModal} onRefresh={refresh}>
 *       {content}
 *     </PullToRefresh>
 *   );
 * }
 * ```
 */
export function useModalState() {
  const context = useContext(ModalStateContext);
  
  if (!context) {
    throw new Error('useModalState deve ser usado dentro de um ModalStateProvider');
  }
  
  return context;
}

/**
 * Hook para registrar um modal e gerenciar seu ciclo de vida
 * 
 * @param modalId - ID único do modal
 * @param isOpen - Estado de abertura do modal
 * 
 * @example
 * ```tsx
 * function MyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
 *   useModalRegistration('my-modal', open);
 *   
 *   return <Modal open={open} onClose={onClose}>...</Modal>;
 * }
 * ```
 */
export function useModalRegistration(modalId: string, isOpen: boolean) {
  const { openModal, closeModal } = useModalState();

  // Registrar/desregistrar modal baseado no estado
  useEffect(() => {
    if (isOpen) {
      openModal(modalId);
      return () => closeModal(modalId);
    }
  }, [isOpen, modalId, openModal, closeModal]);
}
